import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useTranslationsStore = defineStore('translations', () => {
  const translations = ref<Record<string, string>>({})
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isLoaded = ref(false)

  const translate = computed(() => {
    return (key: string): string => {
      if (!key) {
        return ''
      }

      // if key starts with *, no need to translate
      if (key.startsWith('*')) {
        return key.slice(1).trim()
      }

      // if key does not start with #, return it as-is
      if (!key.startsWith('#')) {
        return key
      }

      // remove the leading # to match the key in the JSON file
      const translationKey = key.slice(1)
      // Return the translation if it exists, otherwise return the original key
      return (translations.value[translationKey]?.trim() || key)
    }
  })

  async function loadTranslations(language: string = 'de') {
    if (isLoaded.value) {
      return // Already loaded
    }

    isLoading.value = true
    error.value = null

    // Create AbortController for timeout (iOS 15 compatible)
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    try {
      const response = await fetch(`https://api.dinoconnect.de/api/v1/langs/${language}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        // Add timeout to prevent hanging requests
        signal: controller.signal
      })
      
      // Clear timeout if request completes successfully
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        throw new Error(`Failed to load translations: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      // Validate that we received an object with string values
      if (typeof data !== 'object' || data === null) {
        throw new Error('Invalid translation data received from API')
      }

      translations.value = data
      isLoaded.value = true
      console.log(`Translations loaded successfully for language: ${language}`)
    } catch (err) {
      // Clear timeout in case of error
      clearTimeout(timeoutId)
      
      error.value = err instanceof Error ? err.message : 'Failed to load translations'
      console.error('Error loading translations:', err)
      
      // Fallback to empty object - translate function will return keys as-is
      translations.value = {}
      isLoaded.value = true // Mark as loaded even with fallback to prevent retries
    } finally {
      isLoading.value = false
    }
  }

  async function reloadTranslations(language: string = 'de') {
    reset()
    await loadTranslations(language)
  }

  function reset() {
    translations.value = {}
    isLoading.value = false
    error.value = null
    isLoaded.value = false
  }

  return {
    translations,
    isLoading,
    error,
    isLoaded,
    translate,
    loadTranslations,
    reloadTranslations,
    reset
  }
})
